import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  signOut,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth';
import {
  getApiBase,
  getFirebaseAuth,
  getCurrentEnv,
  switchFirebaseApp,
  ENVIRONMENTS
} from '../config/config';

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Storage keys
const TOKEN_KEY = 'qarl_auth_token';
const USER_KEY = 'qarl_user_data';

const ALLOWED_DOMAIN = 'qarl.ai';

const validateEmailDomain = (email) => {
  if (!email || !email.endsWith(`@${ALLOWED_DOMAIN}`)) {
    throw new Error(`Access restricted to @${ALLOWED_DOMAIN} accounts only.`);
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [environment, setEnvironment] = useState(getCurrentEnv());

  // Load stored token on mount
  useEffect(() => {
    const storedToken = localStorage.getItem(TOKEN_KEY);
    const storedUser = localStorage.getItem(USER_KEY);

    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
    }
    setLoading(false);
  }, []);

  // Verify Firebase ID token with backend (for Google OAuth)
  const verifyWithBackend = async (firebaseIdToken) => {
    const response = await fetch(`${getApiBase()}/auth/verify-oauth`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ idToken: firebaseIdToken }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Backend authentication failed');
    }

    return response.json();
  };

  // Email/password login via server
  const login = async (email, password) => {
    setError(null);
    try {
      validateEmailDomain(email);
      const response = await fetch(`${getApiBase()}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Login failed');
      }

      const authResponse = await response.json();

      // Store token and user data
      localStorage.setItem(TOKEN_KEY, authResponse.token);
      localStorage.setItem(USER_KEY, JSON.stringify(authResponse.user));

      setToken(authResponse.token);
      setUser(authResponse.user);

      return authResponse.user;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Google OAuth via Firebase popup + backend verification
  const loginWithGoogle = async () => {
    setError(null);
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(getFirebaseAuth(), provider);

      // Validate domain before proceeding
      validateEmailDomain(result.user.email);

      const firebaseIdToken = await result.user.getIdToken();

      const authResponse = await verifyWithBackend(firebaseIdToken);

      // Store token and user data
      localStorage.setItem(TOKEN_KEY, authResponse.token);
      localStorage.setItem(USER_KEY, JSON.stringify(authResponse.user));

      setToken(authResponse.token);
      setUser(authResponse.user);

      return authResponse.user;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const logout = async () => {
    try {
      await signOut(getFirebaseAuth());
    } catch (err) {
      // Firebase signout may fail if user logged in via email/password (no Firebase session)
    }

    // Clear stored data
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);

    setUser(null);
    setToken(null);
  };

  const refreshToken = async () => {
    try {
      // Only works for Google OAuth users who have a Firebase session
      const firebaseUser = getFirebaseAuth().currentUser;
      if (firebaseUser) {
        const firebaseIdToken = await firebaseUser.getIdToken(true);
        const authResponse = await verifyWithBackend(firebaseIdToken);

        localStorage.setItem(TOKEN_KEY, authResponse.token);
        localStorage.setItem(USER_KEY, JSON.stringify(authResponse.user));

        setToken(authResponse.token);
        setUser(authResponse.user);

        return authResponse.token;
      }
    } catch (err) {
      console.error('Token refresh failed:', err);
    }
    // If refresh fails or no Firebase session (email/password user), logout
    await logout();
    return null;
  };

  const switchEnvironment = async (envKey) => {
    if (envKey === environment) return;

    // Logout from current environment
    try {
      await signOut(getFirebaseAuth());
    } catch (err) {
      // Ignore
    }

    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);

    // Reinitialize Firebase with new config
    switchFirebaseApp(envKey);

    setUser(null);
    setToken(null);
    setError(null);
    setEnvironment(envKey);
  };

  const value = {
    user,
    token,
    loading,
    error,
    environment,
    environments: ENVIRONMENTS,
    login,
    loginWithGoogle,
    logout,
    refreshToken,
    switchEnvironment
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
